import { forwardRef, memo, useMemo } from "react";
import { Divider } from "@mui/material";
import {
    AllProductAutoSelectRuntimeConfig,
    AllProductMatchedProduct,
    AllProductPrecheckResult,
    AllProductSelectionMeta,
    AllProductPrecheckProgressUpdate,
    PreparedSendRequest,
    QuestionInput,
    QuestionInputRef
} from "../../../components/QuestionInput";
import { AppType } from "../../../api/models";
import { ChatToolbar, ChatToolbarProps } from "./ChatToolbar";
import i18next from "../../../i18n/i18n";
import styles from "../Chat.module.css";

interface ChatInputWrapperProps {
    mode: "chat" | "history";
    clearOnSend: boolean;
    disabled: boolean;
    isAdmin: boolean;
    productIndex: { manual_key: string; faq_key: string };
    onSend: (question: string, questionContainsTemplate: boolean, appTypeValue: AppType) => void;
    templateContent: unknown;
    selectedTemplateContentId: string;
    onClearTemplateContent: () => void;
    disableTextarea: boolean;
    isEdited: boolean;
    setIsEdited: (value: boolean) => void;
    clearChatTriggered: boolean;
    setClearChatTriggered: (value: boolean) => void;
    chatContentCleared: boolean;
    setChatContentCleared: (value: boolean) => void;
    selectedAppType: AppType;
    isCitationActive: boolean;
    isHistoryPageInitialLoading: boolean;
    setIsHistoryPageInitialLoading: (value: boolean) => void;
    changingTemplateContent: boolean;
    setChangingTemplateContent: (value: boolean) => void;
    handleTemplateContentsBySuggestions: (contents: unknown, tempId: string) => void;
    isNewChatThreadInitialLoading: boolean;
    setIsNewChatThreadInitialLoading: (value: boolean) => void;
    answers: {}[];
    setSelectedAppType: (appType: AppType) => void;
    setActiveFileAttachmentTour: (tourFlag: boolean) => void;
    showSupportLink: boolean;
    supportEnvBoard: string;
    toolbarProps: ChatToolbarProps;
    onAllProductPrecheck?: (
        sendRequest: PreparedSendRequest,
        onProgress?: (update: AllProductPrecheckProgressUpdate) => void
    ) => Promise<AllProductPrecheckResult>;
    onAbortAllProductPrecheck?: () => void;
    onAllProductTagSelected?: (
        mode: "chat" | "history",
        selectedProduct: AllProductMatchedProduct,
        sendRequest: PreparedSendRequest,
        selectionMeta?: AllProductSelectionMeta
    ) => Promise<void>;
    allProductAutoSelectConfig?: AllProductAutoSelectRuntimeConfig;
    showInlineAllProductValidationFallback?: boolean;
    clarificationBanner?: { type: "greeting" | "vague" | "off_topic"; message: string } | null;
    onSendAnyway?: () => void;
    onDismissClarification?: (skipValidation?: boolean, softDismiss?: boolean) => void;
}

const ChatInputWrapperComponent = forwardRef<QuestionInputRef, ChatInputWrapperProps>(
    (
        {
            mode,
            clearOnSend,
            disabled,
            isAdmin,
            productIndex,
            onSend,
            templateContent,
            selectedTemplateContentId,
            onClearTemplateContent,
            disableTextarea,
            isEdited,
            setIsEdited,
            clearChatTriggered,
            setClearChatTriggered,
            chatContentCleared,
            setChatContentCleared,
            selectedAppType,
            isCitationActive,
            isHistoryPageInitialLoading,
            setIsHistoryPageInitialLoading,
            changingTemplateContent,
            setChangingTemplateContent,
            handleTemplateContentsBySuggestions,
            isNewChatThreadInitialLoading,
            setIsNewChatThreadInitialLoading,
            answers,
            setSelectedAppType,
            setActiveFileAttachmentTour,
            showSupportLink,
            supportEnvBoard,
            toolbarProps,
            onAllProductPrecheck,
            onAbortAllProductPrecheck,
            onAllProductTagSelected,
            allProductAutoSelectConfig,
            showInlineAllProductValidationFallback = true,
            clarificationBanner,
            onSendAnyway,
            onDismissClarification
        },
        ref
    ) => {
        const onAllProductTagSelectedForMode = useMemo(
            () =>
                onAllProductTagSelected
                    ? (selectedProductOption: AllProductMatchedProduct, sendRequest: PreparedSendRequest, selectionMeta?: AllProductSelectionMeta) =>
                          onAllProductTagSelected(mode, selectedProductOption, sendRequest, selectionMeta)
                    : undefined,
            [mode, onAllProductTagSelected]
        );

        return (
            <div className={mode === "history" ? styles.chatInput2 : styles.chatInput}>
                {showSupportLink && (
                    <h5 className={styles.supportLinkText}>
                        {i18next.t("chat.link_info")}
                        <a className={styles.supportLink} href={supportEnvBoard} target="_blank">
                            {i18next.t("chat.link_text")}
                        </a>
                    </h5>
                )}
                <div style={{ width: "100%", backgroundColor: "white", borderRadius: "10px" }}>
                    <ChatToolbar {...toolbarProps} />
                    <Divider sx={{ width: "97%", margin: "0 auto 4px auto" }} />
                    <div style={{ paddingTop: "2px" }}>
                        <QuestionInput
                            ref={ref}
                            mode={mode}
                            clearOnSend={clearOnSend}
                            disabled={disabled}
                            isAdmin={isAdmin}
                            productIndex={productIndex}
                            onSend={onSend}
                            templateContent={templateContent}
                            selectedTemplateContentId={selectedTemplateContentId}
                            onClearTemplateContent={onClearTemplateContent}
                            disableTextarea={disableTextarea}
                            isEdited={isEdited}
                            setIsEdited={setIsEdited}
                            clearChatTriggered={clearChatTriggered}
                            setClearChatTriggered={setClearChatTriggered}
                            chatContentCleared={chatContentCleared}
                            setChatContentCleared={setChatContentCleared}
                            selectedAppType={selectedAppType}
                            isCitationActive={isCitationActive}
                            isHistoryPageInitialLoading={isHistoryPageInitialLoading}
                            setIsHistoryPageInitialLoading={setIsHistoryPageInitialLoading}
                            changingTemplateContent={changingTemplateContent}
                            setChangingTemplateContent={setChangingTemplateContent}
                            handleTemplateContentsBySuggestions={handleTemplateContentsBySuggestions}
                            isNewChatThreadInitialLoading={isNewChatThreadInitialLoading}
                            setIsNewChatThreadInitialLoading={setIsNewChatThreadInitialLoading}
                            answers={answers}
                            setSelectedAppType={setSelectedAppType}
                            setActiveFileAttachmentTour={setActiveFileAttachmentTour}
                            onAllProductPrecheck={onAllProductPrecheck}
                            onAbortAllProductPrecheck={onAbortAllProductPrecheck}
                            onAllProductTagSelected={onAllProductTagSelectedForMode}
                            allProductAutoSelectConfig={allProductAutoSelectConfig}
                            showInlineAllProductValidationFallback={showInlineAllProductValidationFallback}
                            clarificationBanner={clarificationBanner}
                            onSendAnyway={onSendAnyway}
                            onDismissClarification={onDismissClarification}
                        />
                    </div>
                </div>
            </div>
        );
    }
);

ChatInputWrapperComponent.displayName = "ChatInputWrapper";

export const ChatInputWrapper = memo(ChatInputWrapperComponent);
